import { useEffect, useState } from "react";
import { acrylicStyles, acrylicDialogSurface } from "../../theme/acrylic";
import { getTigerResource } from "../../utils/tigerResource";
import { formatLabel, withExtension, type ReportFormat } from "../../utils/reportFormats";

// The browser's stand-in for the desktop's native save dialog, used by every
// report and export save (Tools > Reports, Window > Save Tools as Report, Export
// Data/Trend, the Diagnostics window's Save Report). A browser can offer no
// folder choice, so this collects a base filename plus one of the formats the
// desktop dialog lists for the same save (utils/reportFormats.ts) and hands back
// `<base>.<ext>`; the caller streams the report from the webserver and the
// browser saves it to Downloads.
//
// Callers normally reach this through `promptReportSave` (utils/reportSavePrompt)
// rather than mounting it themselves.

export interface ReportSaveDialogProps {
  open: boolean;
  /** Resource id for the title bar (e.g. TIGER_SAVE_REPORT_TITLE). */
  titleId: string;
  /** Suggested name without extension. */
  defaultBase: string;
  /** Offered formats; the first is the default unless `defaultExt` matches one. */
  formats: ReportFormat[];
  /** Pre-selected extension (the format remembered from last time). */
  defaultExt?: string;
  onClose: () => void;
  /** Called with `<base>.<ext>` when the user confirms. */
  onSubmit: (filename: string) => void;
}

export function ReportSaveDialog({
  open,
  titleId,
  defaultBase,
  formats,
  defaultExt,
  onClose,
  onSubmit,
}: ReportSaveDialogProps) {
  const initialExt =
    formats.find((f) => f.ext === defaultExt)?.ext ?? formats[0]?.ext ?? "";
  const [baseName, setBaseName] = useState(defaultBase);
  const [ext, setExt] = useState(initialExt);

  // Re-seed when the dialog is (re)opened for a different save.
  useEffect(() => {
    if (!open) return;
    setBaseName(defaultBase);
    setExt(initialExt);
  }, [open, defaultBase, initialExt]);

  if (!open) return null;

  const title = getTigerResource(titleId);
  const trimmed = baseName.trim();
  const single = formats.length === 1;

  const handleDownload = () => {
    if (!trimmed || !ext) return;
    onSubmit(withExtension(trimmed, ext));
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={dialogSurface(formats.length)}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          <button className="acrylic-close-btn" onClick={onClose}>
            &#x2715;
          </button>
        </div>

        <div style={styles.body}>
          <div style={styles.card}>
            <div style={styles.field}>
              <label style={styles.fieldLabel} htmlFor="report-save-name">
                {getTigerResource("TIGER_TOOLREPORT_FILE_NAME")}
              </label>
              <input
                id="report-save-name"
                style={styles.input}
                type="text"
                value={baseName}
                onChange={(e) => setBaseName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleDownload();
                }}
                aria-label={getTigerResource("TIGER_TOOLREPORT_FILE_NAME_ARIA")}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>{getTigerResource("TIGER_TOOLREPORT_FORMAT")}</label>
              {/* One format on offer (Excel): show it, don't ask. */}
              {single ? (
                <span style={styles.singleFormat}>{formatLabel(formats[0])}</span>
              ) : (
                formats.map((f) => (
                  <label key={f.ext} style={styles.radioRow}>
                    <input
                      type="radio"
                      name="report-save-format"
                      value={f.ext}
                      checked={ext === f.ext}
                      onChange={() => setExt(f.ext)}
                    />
                    <span>{formatLabel(f)}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button className="btn-acrylic-secondary" onClick={onClose}>
            {getTigerResource("TIGER_RES_CANCEL")}
          </button>
          <button className="btn-acrylic-primary" onClick={handleDownload} disabled={!trimmed}>
            {getTigerResource("TIGER_RES_DOWNLOAD")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Grow with the format list so six diagnostics formats still fit. */
function dialogSurface(formatCount: number) {
  const height = 250 + Math.max(1, formatCount) * 24;
  return acrylicDialogSurface({ width: 460, height, minWidth: 380, minHeight: 280 });
}

const styles = {
  ...acrylicStyles,
  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    cursor: "pointer",
    padding: "2px 0",
  },
  singleFormat: { fontSize: 13, padding: "2px 0" },
} as const;
